import { useCallback, useEffect, useState } from "react";
import { Target, Loader2, CheckCircle, AlertTriangle, RefreshCw } from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";

/**
 * Medición de Meta (superadmin): Pixel + Conversions API. Registros con
 * consentimiento enviados desde el servidor (registro, primera reserva, pago).
 */

interface MetaTrackingStatus {
  enabled: boolean;
  pixelId: string | null;
  pixelName: string | null;
  last30Days: { consentedSignups: number; withMetaClick: number };
  events: Record<string, number>;
  recentErrors: Array<{ type: string; error: string; at: string }>;
  assetsError: string | null;
}

const TYPES: Array<{ type: string; label: string }> = [
  { type: "signup", label: "Registro (CompleteRegistration)" },
  { type: "activated", label: "Primera reserva (PrimeraReserva)" },
  { type: "paid", label: "Pago (Subscribe)" },
];

export default function MetaConversionsCard() {
  const { showToast } = useGlobalToast();
  const [status, setStatus] = useState<MetaTrackingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ success: true; data: MetaTrackingStatus }>("/admin/meta-ads/conversions/status");
      setStatus(data.data);
      setError(null);
    } catch (err) {
      setError(extractApiErrorMessage(err, "No se pudo cargar la medición de Meta"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post<{ success: true; data: Record<string, number> }>("/admin/meta-ads/conversions/sync");
      const r = data.data;
      showToast(`Sincronizado: ${r.metaDetected ?? 0} nuevas, ${r.metaSent ?? 0} enviadas, ${r.metaFailed ?? 0} con error`, "success");
      await load();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo sincronizar"), "error");
    } finally {
      setSyncing(false);
    }
  }, [load, showToast]);

  const count = (type: string, state: string) => status?.events[`${type}.${state}`] ?? 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-600" /> Medición (Pixel y Conversions API)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Registros con cookies de marketing aceptadas, enviados a Meta desde el servidor para medir y optimizar.
          </p>
        </div>
        <button
          onClick={() => void handleSync()}
          disabled={syncing || !status?.pixelId}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sincronizar ahora
        </button>
      </div>

      {loading && !status ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 mt-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </p>
      ) : error ? (
        <p className="flex items-center gap-2 text-sm text-red-600 mt-4">
          <AlertTriangle className="w-4 h-4" /> {error}
        </p>
      ) : status ? (
        <div className="mt-4 space-y-4">
          {status.assetsError ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2 break-words">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> No se pudo consultar Meta: {status.assetsError}
            </p>
          ) : status.pixelId ? (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-start gap-2 break-words">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> Pixel "{status.pixelName ?? status.pixelId}" ({status.pixelId})
              conectado a la Conversions API.
            </p>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> No hay Pixel asignado al token de Meta: asígnalo al usuario
              del sistema en el Business Manager.
            </p>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            {TYPES.map(({ type, label }) => (
              <div key={type} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 mt-1.5">
                  {count(type, "uploaded")} enviadas · {count(type, "pending")} pendientes · {count(type, "failed")} fallidas
                </p>
              </div>
            ))}
          </div>

          <p className="text-sm text-gray-600">
            Últimos 30 días: <strong>{status.last30Days.consentedSignups}</strong> registros medibles en Meta y{" "}
            <strong>{status.last30Days.withMetaClick}</strong> llegados desde un anuncio de Meta.
          </p>

          {!status.enabled && <p className="text-sm text-amber-700">El envío a la Conversions API está desactivado en el servidor.</p>}

          {status.recentErrors.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Últimos errores</p>
              <ul className="mt-1 space-y-1">
                {status.recentErrors.map((item, index) => (
                  <li key={index} className="text-xs text-gray-600 break-words">
                    <strong>{item.type}:</strong> {item.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
